import { useCallback, useEffect, useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { SupportedChainId } from '~/constants/chains'
import { useOrgContacts } from '~/hooks/useOrgContacts'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import {
  getSendRuleChainIds,
  isAllowedBySendRules,
} from '~/utils/contactSendRules'

/**
 * Applies contact send rules to the transfer form. The token and network
 * pickers show only what every selected recipient accepts. If a new recipient
 * does not accept the selected token, the hook clears the token.
 */
export const useSendRuleEnforcement = () => {
  const { control, setValue, getValues } = useFormContext<TransferFormValues>()
  const recipients = useWatch({ control, name: 'recipients' })
  const transferToken = useWatch({ control, name: 'transferToken' })
  const { contacts } = useOrgContacts()

  const restrictedContacts = useMemo(() => {
    return (recipients ?? []).flatMap((recipient) => {
      const address = recipient?.receiver?.address
      const contact = address
        ? contacts.find((entry) => Address.equal(entry.address, address))
        : undefined
      return contact && contact.sendRules.length > 0 ? [contact] : []
    })
  }, [recipients, contacts])

  const sendRuleTokenFilter = useCallback(
    (token: { chainId: SupportedChainId; address: string }) =>
      restrictedContacts.every((contact) =>
        isAllowedBySendRules({
          sendRules: contact.sendRules,
          tokenAddress: token.address,
          chainId: token.chainId,
        }),
      ),
    [restrictedContacts],
  )

  const sendRuleChainFilter = useCallback(
    (chainId: SupportedChainId) =>
      restrictedContacts.every((contact) => {
        const chains = getSendRuleChainIds(contact.sendRules)
        return !chains || chains.includes(chainId)
      }),
    [restrictedContacts],
  )

  const selectedToken = transferToken?.token
  const selectedChainId = transferToken?.chainId
  const hasSelection =
    !!selectedToken && selectedToken !== '0x' && !!selectedChainId

  const violatedContact = hasSelection
    ? restrictedContacts.find(
        (contact) =>
          !isAllowedBySendRules({
            sendRules: contact.sendRules,
            tokenAddress: selectedToken,
            chainId: selectedChainId,
          }),
      )
    : undefined

  // eslint-disable-next-line local/noUseEffect -- the conflict between recipient and token can come from the picker, a URL prefill, or a new multi-send row
  useEffect(() => {
    if (!violatedContact || !hasSelection) return

    // Clear only the token and the chain. The amount stays. Do not use the
    // '0x' value: a truthy token with no chain causes a crash in consumers
    // that format it.
    setValue('transferToken', {
      token: undefined as unknown as IAddress,
      chainId: undefined as unknown as SupportedChainId,
      amount: getValues('transferToken.amount') ?? '0',
    })
  }, [violatedContact, hasSelection, setValue, getValues])

  return {
    sendRuleTokenFilter,
    sendRuleChainFilter,
    // A restricted recipient accepts only tokens on org chains. The pickers
    // must remove relay networks and apply the filter as a strict allowlist.
    hasSendRuleRestrictions: restrictedContacts.length > 0,
  }
}
